<script setup>
import { computed } from 'vue'
import VueApexCharts from 'vue3-apexcharts'

const props = defineProps({
  chartData: {
    type: Object,
    required: true,
  },
  labelList: {
    type: Array,
    required: true,
  },
  colorMap: {
    type: Object,
    required: true,
  },
  centerTitle: {
    type: String,
    default: '',
  },
  labelDirection: {
    type: String,
    default: 'column', // 'column' or 'row'
  },
  isMobile:{
    type: Boolean,
    default: false,
  }
})

const colors = computed(() => {
  if (!props.labelList || !props.colorMap) return ['#ccc', '#ccc']
  return props.labelList.map(label => props.colorMap[label] ?? '#ccc')
})

const series = computed(() => {
  if (!props.labelList || !props.chartData) return [0, 0]
  return props.labelList.map(label => Number(props.chartData[label]) ?? 0)
})

const centerTitleText = computed(() => props.centerTitle || '')

const chartSize = computed(() => (props.isMobile ? 160 : 200))

const chartOptions = computed(() => ({
  chart: {
    type: 'donut',
    height: chartSize.value,
    width: chartSize.value,
    parentHeightOffset: 0,
  },
  labels: props.labelList,
  colors: colors.value,
  stroke: {
    width: 0,
  },
  legend: {
    show: false,
  },
  plotOptions: {
    pie: {
      donut: {
        size: '50%',
        labels: {
          show: true,
          name: { show: false },
          value: { show: false },
          total: {
            show: true,
            label: '',
            fontSize: '16px',
            fontWeight: 600,
            color: '#333',
            formatter: () => centerTitleText.value, 
          },
        },
      },
    },
  },
  dataLabels: {
    enabled: false,
  },
  tooltip: {
    enabled: true,
    y: {
      formatter: val => `${val}%`,
      title: { formatter: () => '' },
    },
  },
}))
</script>

<template>
<div 
  class="pie-chart__wrapper"
  :class="{mobile: isMobile}"
>
  <div class="donut-chart__container">
    <VueApexCharts
      v-if="series.length > 0"
      :options="chartOptions"
      :series="series"
      type="donut"
      :height="chartSize"
      :width="chartSize"
    />

    <div class="center-title">
      {{ props.centerTitle }}
    </div>
  </div>

  <div
    class="pie-chart__legend"
    :class="[labelDirection === 'row' ? 'row' : 'column']"
  >
    <div
      v-for="(label, idx) in labelList"
      :key="idx"
      class="legend-item"
    >
      <span
        class="legend-dot"
        :style="{ backgroundColor: props.colorMap[label] ?? '#ccc' }"
      />
      <span class="legend-label">{{ label }}</span>
      <!-- <span class="legend-value">{{ chartData[label] ?? 0 }}%</span> -->
    </div>
  </div>
</div>

</template>

<style scoped lang="scss">
.pie-chart__wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
}
.pie-chart__legend {
  display: flex;
  margin-top: 10px;
}
.pie-chart__legend.column {
  flex-direction: column;
  align-items: flex-start;
}
.pie-chart__legend.row {
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: center;
}
.legend-item {
  display: flex;
  align-items: center;
  margin: 4px 8px;
  font-size: 14px;
}
.legend-dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-right: 6px;
}
.legend-label {
  margin-right: 4px;
  color: var(--Gray-800, #333);
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 18px;
}
.legend-value {
  font-weight: 600;
}

.donut-chart__container {
  position: relative;
  display: inline-block;
}

.center-title {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: var(--Gray-800, #333);
  text-align: center;
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 20px;
  pointer-events: none;
}

</style>
